/**
 * useNodeExecution — Shared hook for execution-based node processing.
 * Encapsulates: file upload → queue execution → poll status → return result.
 *
 * Polling: controlled via AbortController, stops on terminal states,
 * cleans up on unmount. No uncontrolled setInterval.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNodeId } from 'reactflow';
import axios from 'axios';
import { useCreditsStore } from '../store/creditsStore';
import { useWorkflowStore } from '../store/workflowStore';
import { useBatchStore } from '../store/batchStore';
import { askNodeBusy } from '../services/nodeBusy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL = 2500;
// Sin corte forzado por tiempo: el nodo espera vivo mientras el job siga en
// queued/processing (los avatares Kling pueden tardar ~18 min). El backend
// garantiza un estado terminal vía recuperación de jobs stale (30 min).
// Sólo se abandona tras errores de red CONSECUTIVOS (inestabilidad real).
const MAX_CONSECUTIVE_POLL_ERRORS = 10;

export const useNodeExecution = () => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [executionId, setExecutionId] = useState(null);
  const [insufficientCredits, setInsufficientCredits] = useState(false);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  const refreshAfterExecution = useCreditsStore(s => s.refreshAfterExecution);
  const hasEnoughCredits = useCreditsStore(s => s.hasEnoughCredits);
  const getCostForType = useCreditsStore(s => s.getCostForType);
  const fetchQuote = useCreditsStore(s => s.fetchQuote);
  const balance = useCreditsStore(s => s.balance);
  const openBuyModal = useCreditsStore(s => s.openBuyModal);

  const currentProjectId = useWorkflowStore(s => s.currentProjectId);
  // Id del nodo desde el contexto de React Flow: sirve para los 25 nodos sin
  // tocar ninguno de sus componentes (E6c.5).
  const nodeId = useNodeId();

  // Cleanup on unmount — abort any in-flight polling
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const safeSetState = useCallback((setter, value) => {
    if (mountedRef.current) setter(value);
  }, []);

  const logTransition = useCallback(() => {
    // Production: logging disabled
  }, []);

  const reset = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    safeSetState(setStatus, 'idle');
    safeSetState(setError, null);
    safeSetState(setExecutionId, null);
    safeSetState(setInsufficientCredits, false);
  }, [safeSetState]);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    safeSetState(setStatus, 'idle');
  }, [safeSetState]);

  /**
   * Upload files and get URLs.
   */
  const uploadFiles = async (files, signal) => {
    const urls = {};
    for (const { key, file } of files) {
      if (!file) continue;
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
      });
      urls[key] = res.data.url;
    }
    return urls;
  };

  /**
   * Controlled polling — uses setTimeout chain (not setInterval).
   * Stops on: terminal state, abort signal, unmount, or repeated network
   * errors. NO hay timeout por duración: un job generando sigue esperando.
   */
  const pollUntilDone = (execId, signal) => {
    return new Promise((resolve, reject) => {
      let pollCount = 0;
      let consecutiveErrors = 0;

      const doPoll = async () => {
        if (signal.aborted) return resolve(null);
        if (!mountedRef.current) return resolve(null);

        try {
          pollCount++;
          const res = await axios.get(`${API}/executions/${execId}`, { signal });
          consecutiveErrors = 0;
          const exec = res.data.execution;

          if (exec.status === 'queued' || exec.status === 'processing') {
            if (mountedRef.current) {
              setStatus(prev => {
                if (prev !== exec.status) logTransition(prev, exec.status, execId);
                return exec.status;
              });
            }
            // Schedule next poll via setTimeout (controlled, not setInterval)
            setTimeout(doPoll, POLL_INTERVAL);
            return;
          }

          // Terminal state
          resolve(exec);
        } catch (err) {
          if (err.name === 'CanceledError' || signal.aborted) {
            resolve(null);
            return;
          }
          // Errores transitorios de red NO cortan la espera: se reintenta.
          consecutiveErrors += 1;
          if (consecutiveErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
            reject(new Error('No se pudo consultar el estado de la generación (red inestable). La generación puede seguir corriendo: revisá el Historial de ejecuciones.'));
            return;
          }
          setTimeout(doPoll, POLL_INTERVAL * 2);
        }
      };

      // First poll after a short delay
      setTimeout(doPoll, 1000);
    });
  };

  /**
   * Main processing function.
   */
  const processNode = useCallback(async ({ resultType, files = [], params = {}, projectId = null }) => {
    // Abort any previous execution
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const { signal } = controller;

    safeSetState(setError, null);
    safeSetState(setExecutionId, null);
    safeSetState(setInsufficientCredits, false);

    // Resolve project_id: explicit param > store > fail
    const resolvedProjectId = projectId || currentProjectId;
    if (!resolvedProjectId) {
      safeSetState(setStatus, 'failed');
      safeSetState(setError, 'Selecciona o crea un proyecto antes de ejecutar.');
      return null;
    }

    // Pre-execution credit check (universal — all roles honor balance).
    // Envío 4: el costo sale de /credits/quote (dinámico si el flag está
    // prendido, fijo si no); la tabla fija sólo queda de respaldo offline.
    const quote = await fetchQuote(resultType, params);
    const quotedCost = quote?.credits;
    const cost = typeof quotedCost === 'number' ? quotedCost : getCostForType(resultType);
    if (quote?.mode === 'blocked') {
      safeSetState(setStatus, 'failed');
      safeSetState(setError, 'Esta combinación todavía no tiene precio.');
      return null;
    }
    if (balance !== null && balance < cost) {
      const msg = `Créditos insuficientes. Necesitas ${cost} créditos (tienes ${balance ?? 0}).`;
      safeSetState(setStatus, 'failed');
      safeSetState(setError, msg);
      safeSetState(setInsufficientCredits, true);
      return null;
    }

    try {
      // Step 1: Upload files
      let fileUrls = {};
      if (files.length > 0) {
        logTransition(status, 'uploading', null);
        safeSetState(setStatus, 'uploading');
        fileUrls = await uploadFiles(files, signal);
      }

      if (signal.aborted) return null;

      // Step 2: Queue execution
      logTransition('uploading', 'queued', null);
      safeSetState(setStatus, 'queued');
      const inputParams = { ...params, ...fileUrls };
      const body = {
        result_type: resultType,
        input_params: inputParams,
        project_id: resolvedProjectId,
      };
      // E6c.5 · la ejecución queda atada al nodo: nunca queda sin dueño y el
      // backend puede avisar si ya hay otra en curso.
      if (nodeId) body.node_id = nodeId;
      // Attach batch trace metadata if a batch run is active. Only serializable
      // primitive fields — the backend guards against user_role spoofing.
      const batchCtx = useBatchStore.getState().activeContext;
      if (batchCtx && batchCtx.batch_id) {
        body.metadata = {
          batch_id: batchCtx.batch_id,
          batch_item_index: batchCtx.batch_item_index,
          batch_node_id: batchCtx.batch_node_id ?? null,
        };
      }

      // Aviso local antes de mandar (botón del nodo, Regenerar, lote). Si el
      // arranque del flujo ya preguntó por este nodo, no se vuelve a preguntar.
      if (nodeId) {
        const store = useWorkflowStore.getState();
        const alreadyConfirmed = store.consumeBusyConfirmed(nodeId);
        if (!alreadyConfirmed) {
          const answer = await store.confirmBusyNodes([nodeId], {
            cost,
            source: 'node',
          });
          if (answer === 'cancel') {
            safeSetState(setStatus, 'idle');
            return { cancelled: true };
          }
          if (answer === 'send') body.confirm_parallel = true;
        } else {
          body.confirm_parallel = true;
        }
      }

      let exec;
      try {
        const queueRes = await axios.post(`${API}/executions`, body, { signal });
        exec = queueRes.data.execution;
      } catch (err) {
        // Envío 4 · combinación sin precio cargado: mensaje claro, sin reintento.
        const unpriced = err.response?.status === 402
          && err.response.data?.detail?.code === 'unpriced';
        if (unpriced) {
          safeSetState(setStatus, 'failed');
          safeSetState(setError, err.response.data.detail.message);
          return null;
        }
        const detail = err.response?.status === 409 ? err.response.data?.detail : null;
        if (detail?.code !== 'node_busy') throw err;
        // El servidor vio generaciones que el canvas no tenía (otra pestaña,
        // recarga, LMX): mismo aviso, con sus datos.
        const running = (detail.active || []).length;
        const answer = await askNodeBusy({
          nodes: [{ nodeId, running, label: resultType }],
          cost: detail.cost,
          source: 'server',
        });
        if (answer === 'cancel') {
          safeSetState(setStatus, 'idle');
          return { cancelled: true };
        }
        if (answer === 'stop_and_send') {
          const { stopExecution } = await import('../services/stopExecution');
          for (const a of detail.active || []) {
            // eslint-disable-next-line no-await-in-loop
            await stopExecution(a.execution_id, { confirm: false });
          }
          useWorkflowStore.getState().setNodeInFlight(nodeId, []);
          window.dispatchEvent(new Event('executions:changed'));
        }
        const retryRes = await axios.post(
          `${API}/executions`, { ...body, confirm_parallel: true }, { signal });
        exec = retryRes.data.execution;
      }

      safeSetState(setExecutionId, exec.id);
      if (nodeId) {
        const store = useWorkflowStore.getState();
        // El nodo muestra SIEMPRE la última generación mandada.
        store.setNodeActiveExecution(nodeId, exec.id);
        store.addInFlight(nodeId, {
          execution_id: exec.id, status: 'queued',
          result_type: resultType, created_at: new Date().toISOString(),
        });
        window.dispatchEvent(new Event('executions:changed'));
      }

      if (signal.aborted) return null;

      // Step 3: Poll until done (controlled setTimeout chain)
      const finalExec = await pollUntilDone(exec.id, signal);

      if (!finalExec) {
        safeSetState(setStatus, 'idle');
        return null;
      }

      if (finalExec.status === 'completed') {
        logTransition('processing', 'completed', exec.id);
        const results = finalExec.results || [];
        const primary = results[0] || {};
        const outputUrl = primary.output_url || null;
        // Identidad persistente del resultado: el nodo guarda el asset_id y
        // deja de depender del snapshot de URL.
        const execAssets = finalExec.assets || [];
        const outputAsset = execAssets.find((a) => a.role === 'output') || execAssets[0] || null;
        const assetId = primary.output_asset_id || outputAsset?.id || null;
        // Refresh credit balance after successful execution
        refreshAfterExecution();
        if (nodeId) {
          const store = useWorkflowStore.getState();
          store.removeInFlight(nodeId, exec.id);
          window.dispatchEvent(new Event('executions:changed'));
          if (!store.isActiveExecution(nodeId, exec.id)) {
            // Una generación anterior que terminó tarde: NO pisa el nodo, va
            // sólo a la galería y el nodo avisa que hay una más.
            store.bumpGalleryNotice(nodeId);
            safeSetState(setStatus, 'idle');
            return { superseded: true };
          }
        }
        safeSetState(setStatus, 'completed');
        return { outputUrl, assetId, execution: finalExec };
      }

      if (finalExec.status === 'cancelled') {
        // La detuvieron (desde Assets → Ejecuciones o el panel MCP): el nodo
        // vuelve a su estado normal, sin error y sin cobro.
        logTransition(status, 'cancelled', exec.id);
        if (nodeId) {
          useWorkflowStore.getState().removeInFlight(nodeId, exec.id);
          window.dispatchEvent(new Event('executions:changed'));
        }
        safeSetState(setStatus, 'idle');
        safeSetState(setError, null);
        refreshAfterExecution();
        return null;
      }

      if (finalExec.status === 'failed') {
        const msg = finalExec.error_message || 'Execution failed';
        logTransition('processing', 'failed', exec.id);
        if (nodeId) {
          const store = useWorkflowStore.getState();
          store.removeInFlight(nodeId, exec.id);
          window.dispatchEvent(new Event('executions:changed'));
          if (!store.isActiveExecution(nodeId, exec.id)) {
            // Falló una vieja: el nodo sigue esperando la suya, sin tocar nada.
            return { superseded: true };
          }
        }
        safeSetState(setStatus, 'failed');
        safeSetState(setError, msg);
        console.error(`[Execution] Failed: ${exec.id} error=${msg}`);
        // Refresh balance even on failure (credits may not have been deducted)
        refreshAfterExecution();
        return null;
      }

      return null;
    } catch (err) {
      if (err.name === 'CanceledError' || signal.aborted) {
        safeSetState(setStatus, 'idle');
        return null;
      }
      // Handle 402 (insufficient credits) specifically
      if (err.response?.status === 402) {
        const detail = err.response.data?.detail || {};
        const msg = typeof detail === 'object'
          ? `Créditos insuficientes. Necesitas ${detail.cost || '?'} créditos (tienes ${detail.balance ?? 0}).`
          : String(detail);
        safeSetState(setStatus, 'failed');
        safeSetState(setError, msg);
        safeSetState(setInsufficientCredits, true);
        refreshAfterExecution();
        return null;
      }
      const msg = err.response?.data?.detail || err.message || 'Error processing';
      const detail = typeof msg === 'object' ? msg.message || 'Error' : msg;
      logTransition('*', 'failed', null);
      safeSetState(setStatus, 'failed');
      safeSetState(setError, detail);
      console.error(`[Execution] Error:`, detail, err);
      return null;
    }
  }, [safeSetState, logTransition, status, fetchQuote, getCostForType, balance, refreshAfterExecution, currentProjectId, nodeId]);

  const stop = useCallback(async () => {
    if (!executionId) return false;
    const { stopExecution } = await import('../services/stopExecution');
    const ok = await stopExecution(executionId);
    if (ok) {
      window.dispatchEvent(new Event('executions:changed'));
      safeSetState(setStatus, 'idle');
    }
    return ok;
  }, [executionId, safeSetState]);

  return { processNode, status, error, executionId, reset, cancel, stop, insufficientCredits, openBuyModal };
};

export default useNodeExecution;
